/* eslint-disable no-console */
// @flow

import { delay } from 'lodash';

import {
  SourceType,
  isSlingSourceType,
  isSlingLiveSourceType,
  isLiveLinearSourceType,
  isDvrSourceType,
} from 'Common/models/franchise';

import { CastReceiver, ChromeCastReceiver } from 'Browser/cast/receiver';
import { PlayerEvent } from 'Browser/player/BitmovinPlayer';

// eslint-disable-next-line camelcase
import { ChromeCastSlingPlayer } from 'Browser/player/ChromeCastSlingPlayer';

import type { AustinEvent, AustinProgramInfoRequest } from 'Common/cast/types';
import type { SourceType as SourceTypeT } from 'Common/player/types';

// $FlowFixMe - direct import (instead of conditional "require") breaks webpack build) */
// const Austin = process.env.IN_CHROMECAST_MODE ? require('austinjs').default : {};
const Austin = process.env.IN_CHROMECAST_MODE ? require('Common/player/assets/austin.debug').default : {};
// Common/player/assets/austin.debug

const DVR_INFO_SYNC_TIMEOUT_MS = 5000;

const LL_INFO_SYNC_TIMEOUT_MS = 5 * 1000;
const LL_INFO_FREQUENT_SYNC_TIMEOUT_MS = 1.5 * 1000;

class CastPlayerHandler {
  receiver: ChromeCastReceiver;
  playerKey: string;
  dvrInfoSyncIntervalID: IntervalID;

  onAustinStateChange: (event: AustinEvent) => void;
  sourceType: SourceTypeT = 'none';
  slingPlayer: ?Object;
  slingPlayerHandler: ChromeCastSlingPlayer; // eslint-disable-line camelcase

  passEventToStateMachine: string => void;
  uiManager: ?Object; /* Instance of Bitmovin UI manager */
  syncLiveLinearInfoIntervalID: IntervalID;
  hideUiTimeoutID: ?TimeoutID = undefined;

  setPlayerStream: Object => void;
  setDVRStream: Object => void;
  setLiveStream: Object => void;

  constructor(
    receiver: ChromeCastReceiver,
    playerKey: string,
    setPlayerStream: Object => void,
    setDVRStream: Object => void,
    setLiveStream: Object => void,
    passEventToStateMachine: string => void,
    onAustinStateChange: (event: AustinEvent) => void,
  ) {
    this.receiver = receiver;
    this.passEventToStateMachine = passEventToStateMachine;
    this.onAustinStateChange = onAustinStateChange;

    this.setPlayerStream = setPlayerStream;
    this.setDVRStream = setDVRStream;
    this.setLiveStream = setLiveStream;

    this.slingPlayerHandler = new ChromeCastSlingPlayer(
      playerKey,
      this.handleAustinEvent,
      this.onAustinDisconnect,
      passEventToStateMachine,
    );
  }

  loadStream = async (
    stream: Object,
    receiverId: string = '',
    uuid: string = '',
    isAustinConfigure: boolean = false,
  ): Promise<void> => {
    const { sourceType } = stream;
    const playingSling = isSlingSourceType(this.sourceType);
    const willPlaySling = isSlingSourceType(sourceType);

    this.sourceType = sourceType;
    ChromeCastReceiver.sendMessageToAllSenders({
      type: 'CURRENT_SOURCE_TYPE',
      payload: { sourceType: this.sourceType },
    });

    this.setCurrentPlayerStream(sourceType, stream);

    if (!isAustinConfigure) await this.destroyCurrentPlayer(playingSling, willPlaySling);

    if (willPlaySling) this.loadSlingPlayer(stream, receiverId, uuid, isAustinConfigure);
    if (playingSling && !willPlaySling) this.sendInfoAboutSubtitlesToSenders();

    if (isLiveLinearSourceType(sourceType)) {
      /* Sender ui on web expects this info in order to re-initialize UI */
      delay(() => this.scheduleLiveLinearInfoSync(LL_INFO_SYNC_TIMEOUT_MS), 10 * 1000);
    } else {
      this.unScheduleLiveInfoSendersSync();
    }
  };

  setCurrentPlayerStream(sourceType: SourceType, stream: Object) {
    if (isSlingLiveSourceType(sourceType)) this.setLiveStream(stream);
    else if (isDvrSourceType(sourceType)) this.setDVRStream(stream);
    else this.setPlayerStream(stream);
  }

  async destroyCurrentPlayer(playingSling: boolean, willPlaySling: boolean) {
    /* Disconnect Austin when processing LOAD and already playing Sling,
    as well as when switching from non-Sling to Sling (also using LOAD) */
    if (playingSling) {
      await this.slingPlayerHandler.destroy();
      this._destroyBitmovinUi();
      this.slingPlayer = undefined;
    } else if (playingSling === false && willPlaySling === true) {
      await CastReceiver.resetMediaElement();
    }
  }

  async loadSlingPlayer(
    {
      finderId,
      slingAdmin,
      sourceType,
      dvrId,
      dvrProgress,
      channelId,
    }: {
      finderId: string,
      slingAdmin: string,
      sourceType: string,
      dvrId: number,
      dvrProgress: number,
      channelId: number,
    },
    receiverId: string,
    uuid: string,
    isAustinConfigure: boolean,
  ) {
    const isDvrSource = isDvrSourceType(sourceType);

    const playConfig = isDvrSource
      ? this.getDvrAustinPlayConfig(dvrId, dvrProgress)
      : this.getLiveAustinPlayConfig(channelId);

    const slingPlayer = await this.slingPlayerHandler.playStream(
      finderId,
      slingAdmin,
      playConfig,
      receiverId,
      uuid,
    );

    if (slingPlayer) {
      if (isDvrSource) this.scheduleDvrInfoSync(DVR_INFO_SYNC_TIMEOUT_MS);
      if (!isAustinConfigure) {
        this.slingPlayer = slingPlayer;
        this.subscribeToAddSubtitlesEvent(slingPlayer);
        this.monkeyPatchBitmovinForSling(slingPlayer, isDvrSource);
        this._setupBitmovinUi(slingPlayer);
      }
      if (process.env.IN_DEBUG) window.player = slingPlayer;
    }
  }

  _setupBitmovinUi = slingPlayer => {
    if (this.uiManager) return;
    this.uiManager = window.bitmovin.playerui.UIFactory.buildDefaultCastReceiverUI(slingPlayer);

    const { shadowRoot } = document.getElementsByTagName('cast-media-player')[0];
    if (!shadowRoot) return;

    const subtitlesContainer = shadowRoot.querySelector('.bmpui-ui-uicontainer');
    const appContainer = document.querySelector('.App-header');

    if (appContainer && subtitlesContainer) appContainer.appendChild(subtitlesContainer);
  };

  _destroyBitmovinUi = () => {
    if (this.uiManager) {
      this.uiManager.release();
      this.uiManager = undefined;
    }
  };

  getDvrAustinPlayConfig = (dvrId: number, dvrProgress: number) => ({
    contentType: 'dvr',
    dvrId,
    dvrProgress,
  });

  getLiveAustinPlayConfig = (channelId: number) => ({
    contentType: 'live',
    inputType: 'service_id',
    inputId: channelId,
  });

  /* Sends dvr info to all senders in order to synchronize DVR info between senders and receiver */
  scheduleDvrInfoSync(timeout: number) {
    clearInterval(this.dvrInfoSyncIntervalID);

    this.dvrInfoSyncIntervalID = setInterval(() => {
      const isDvr = isDvrSourceType(this.sourceType);
      const finishedPlayback = this.slingPlayerHandler.isFinishedPlaying();

      if (isDvr) this.slingPlayerHandler.sendDvrInfoToSenders();

      if (!isDvr || finishedPlayback) {
        if (finishedPlayback)
          CastReceiver.log('[CAST][AUSTIN] Dismissing DVR info sync - DVR finished');
        else
          CastReceiver.log('[CAST][AUSTIN] Dismissing DVR info sync - playback type has changed');

        clearInterval(this.dvrInfoSyncIntervalID);
      }
    }, timeout);
  }

  monkeyPatchBitmovinForSling = (slingPlayer: Object, isDvr: boolean) => {
    slingPlayer.isLive = () => false;
    if (isDvr) return;
    slingPlayer.getCurrentTime = () => Austin.getCurrentTime() / 1000;
    slingPlayer.getDuration = () => Austin.getDuration() / 1000;
    slingPlayer.getVideoBufferLength = () => slingPlayer.getDuration();
    slingPlayer.getAudioBufferLength = () => 0;
  };

  subscribeToAddSubtitlesEvent = (player: Object) => {
    player.on(PlayerEvent.SubtitleAdded, ({ subtitle }: Object) => {
      CastReceiver.log('[CAST][SUBTITLES] Subtitles track have been added: ', subtitle);
      this.sendInfoAboutSubtitlesToSenders();
      this.tweakSubtitlesContainerToMakeItWorkProperly();
    });
  };

  getSlingSubtitleTracks = () => {
    if (this.slingPlayer && this.slingPlayer.subtitles) {
      return this.slingPlayer.subtitles.list() || [];
    }
    return [];
  };

  sendInfoAboutSubtitlesToSenders = () => {
    const tracks = this.getSlingSubtitleTracks();
    const msg = { type: 'SUBTITLES_INFO', payload: { tracks } };

    const onSend = (senderId: string) =>
      CastReceiver.log(`[CAST] Subtitles info has been sent to ${senderId}: `, msg.payload.tracks);
    ChromeCastReceiver.sendMessageToAllSenders(msg, onSend);
  };

  /* For some reason, BM UI container sometimes hides container that renders subtitles.
     This workaround fixes this issue. */
  tweakSubtitlesContainerToMakeItWorkProperly() {
    const subtitlesContainer = document.querySelector('.bmpui-ui-uicontainer');

    if (subtitlesContainer) {
      subtitlesContainer.style.cssText = 'display:block !important; visibility:visible !important';
      CastReceiver.log(
        '[CAST][SUBTITLES] Tweaked subtitles container - subtitles should display properly.',
      );
    } else
      console.warn(
        '[CAST][SUBTITLES] Fail to tweak subtitles container - subtitles display may be incorrect.',
      );
  }

  handleAustinEvent = (event: AustinEvent) => {
    this.onAustinStateChange(event);

    if (event.type === 'onError') {
      console.warn('[CAST][AUSTIN] Destroying player and player UI.');
      this.slingPlayerHandler.destroy();
      this._destroyBitmovinUi();
    }
  };

  seekTo(to: number) {
    if (isSlingSourceType(this.sourceType)) this._handleSlingSeekTo(to);
  }

  seekBy(by: number) {
    if (isSlingSourceType(this.sourceType)) this._handleSlingSeekBy(by);
  }

  _handleSlingSeekTo(to: number) {
    if (!this.slingPlayerHandler) {
      console.warn('[CAST] Fail to handle seek request - slingPlayerHandler is not initialized.');
      return;
    }

    CastReceiver.log('[CAST][AUSTIN] seek to ', to);
    this.slingPlayerHandler.seekTo(to);
  }

  _handleSlingSeekBy(by: number) {
    if (!this.slingPlayerHandler) {
      console.warn('[CAST] Fail to handle seek request - slingPlayerHandler is not initialized.');
      return;
    }

    CastReceiver.log('[CAST][AUSTIN] seek by ', by);
    this.slingPlayerHandler.seek(by);
  }

  onAustinDisconnect = () => this.passEventToStateMachine('idle');

  sendDvrInfoToSenders() {
    if (this.slingPlayerHandler) this.slingPlayerHandler.sendDvrInfoToSenders();
  }

  getAustinProgramInfo = (): Promise<AustinProgramInfoRequest> =>
    this.slingPlayerHandler.getProgramInfo();

  scheduleLiveLinearInfoSync = (timeout: number = LL_INFO_SYNC_TIMEOUT_MS) => {
    this.unScheduleLiveInfoSendersSync();
    CastReceiver.log('[CAST] Scheduling ll info sync');

    this.syncLiveLinearInfoIntervalID = setInterval(this.sendLLInfoToSenders, timeout);
  };

  sendLLInfoToSenders = () => {
    const duration = this.receiver.playerManager.getDurationSec();
    const position = this.receiver.playerManager.getCurrentTimeSec();

    const msg = {
      type: 'LL_INFO',
      payload: {
        timeShift: position - duration,
        maxTimeShift: -duration,
        // Maybe add this somehow later
        bufferedRanges: [{ end: 0, start: 0 }],
      },
    };
    const onSend = (senderId: string) =>
      CastReceiver.log(`[CAST] Sent LL info to "${senderId}"`, msg);

    ChromeCastReceiver.sendMessageToAllSenders(msg, onSend);
  };

  unScheduleLiveInfoSendersSync = () => clearInterval(this.syncLiveLinearInfoIntervalID);

  onRequestPause() {
    if (isLiveLinearSourceType(this.sourceType)) {
      /* TimeShift frequently updates when LL pauses */
      this.scheduleLiveLinearInfoSync(LL_INFO_FREQUENT_SYNC_TIMEOUT_MS);
    }
    if (isSlingSourceType(this.sourceType)) {
      this.slingPlayerHandler.requestPause();
    }
    this.showPlayerUi();
  }

  onRequestPlay() {
    if (isLiveLinearSourceType(this.sourceType)) {
      this.scheduleLiveLinearInfoSync(LL_INFO_SYNC_TIMEOUT_MS);
    }
    if (isSlingSourceType(this.sourceType)) {
      this.slingPlayerHandler.requestResume();
    }
  }

  timeShift(offset: number) {
    if (offset > 0) return;
    if (this.slingPlayer && isSlingSourceType(this.sourceType)) {
      this.slingPlayer.timeShift(offset);
    } else if (isLiveLinearSourceType(this.sourceType)) {
      const duration = this.receiver.playerManager.getDurationSec();
      // offset is a negative offset from the live edge in seconds
      this.receiver.playerManager.seek(duration + offset);
    } else {
      const { sourceType } = this;
      console.warn(
        `[CAST][PLAYER HANDLER] Fail to timeShift. Current sourceType is not "LIVE" - ${sourceType}`,
      );
    }
  }

  pauseLL() {
    if (isLiveLinearSourceType(this.sourceType)) {
      this.receiver.playerManager.pause();
    }
  }

  resumeLL() {
    if (isLiveLinearSourceType(this.sourceType)) {
      this.receiver.playerManager.play();
    }
  }

  showPlayerUiAndScheduleAutoHide = () => {
    this.showPlayerUi();
    this.scheduleUiHide();
  };

  showPlayerUi = () => {
    this.clearHideUiTimeout();

    if (this.uiManager) this.uiManager.currentUi.getUI().show();
    else console.warn('[CAST][PLAYER HANDLER] Fail to show player ui.');
  };

  hidePlayerUI = () => {
    if (!this.hideUiTimeoutID) {
      if (this.uiManager) this.uiManager.currentUi.getUI().hide();
      else console.warn('[CAST][PLAYER HANDLER] Fail to hide player ui.');
    }
  };

  scheduleUiHide = () => {
    if (this.hideUiTimeoutID) {
      clearTimeout(this.hideUiTimeoutID);
      this.hideUiTimeoutID = undefined;
    }

    this.hideUiTimeoutID = setTimeout(this.hidePlayerUI, 3 * 1000);
  };

  clearHideUiTimeout = () => {
    if (this.hideUiTimeoutID) clearTimeout(this.hideUiTimeoutID);
  };
}

export { CastPlayerHandler };
